import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router'
import { get, type AdminUser } from '../api'
import { Notice, Panel } from '../components/ui'
import { NODES_KEY, fetchNodes, mbps, outOfSync } from './Nodes'

const grafana = import.meta.env.VITE_GRAFANA_BASE ?? '/grafana'
// Panels of the "m15-admin" dashboard (deploy/prod/grafana).
const charts = [
  { id: 1, title: 'Outgoing traffic' },
  { id: 2, title: 'Outputs by state' },
  { id: 3, title: 'Server CPU' },
  { id: 4, title: 'Server memory' },
]

function Stat({ label, value, note }: { label: string; value: number | string; note?: string }) {
  return (
    <div className="rounded-[0.5rem] border border-line bg-panel px-4 py-3">
      <p className="text-sm text-muted">{label}</p>
      <p className="num font-sans text-3xl font-semibold">{value}</p>
      {note && <p className="text-xs text-muted">{note}</p>}
    </div>
  )
}

const order = ['live', 'starting', 'reconnecting', 'failed', 'waiting', 'stopped']

export function AdminOverview() {
  // Fleet summary from GET /api/admin/nodes (the single-node GET /api/admin/node is gone).
  const q = useQuery({ queryKey: NODES_KEY, queryFn: fetchNodes, refetchInterval: 5000 })
  const users = useQuery({ queryKey: ['admin-users'], queryFn: () => get<AdminUser[]>('/api/admin/users'), refetchInterval: 15000 })
  const nodes = q.data
  const active = nodes?.filter((n) => n.status !== 'revoked') ?? []
  const online = active.filter((n) => n.status === 'online')
  const outputs: Record<string, number> = {}
  for (const n of active) for (const [s, c] of Object.entries(n.outputs ?? {})) outputs[s] = (outputs[s] ?? 0) + c
  const totalOutputs = Object.values(outputs).reduce((a, b) => a + b, 0)
  const streams = active.reduce((a, n) => a + n.streams, 0)
  const egress = online.reduce((a, n) => a + (n.egress_bps ?? 0), 0)
  const down = active.filter((n) => n.status === 'offline')
  const unsynced = active.filter(outOfSync)
  const u = users.data
  return (
    <div className="space-y-6">
      {q.error && <Notice>{(q.error as Error).message}</Notice>}
      {!!down.length && (
        <Notice>
          <span data-testid="overview-offline">
            Offline: {down.map((n) => n.name).join(', ')}. Streams on {down.length === 1 ? 'it' : 'them'} show as unknown until the node is back.
          </span>
        </Notice>
      )}
      {!!unsynced.length && (
        <p data-testid="overview-unsynced" role="status" className="rounded-md border border-warn/50 bg-warn/[.07] px-3 py-2 text-sm text-warn">
          ▲ Outputs not confirmed by {unsynced.map((n) => n.name).join(', ')}.
        </p>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div data-testid="overview-nodes">
          <Stat label="Nodes online" value={nodes ? `${online.length} / ${active.length}` : '—'} note={nodes ? `${active.filter((n) => !n.accept_ingest).length} out of rotation` : undefined} />
        </div>
        <div data-testid="overview-streams">
          <Stat label="Live streams" value={nodes ? streams : '—'} />
        </div>
        <div data-testid="overview-outputs">
          <Stat label="Outputs on air" value={nodes ? (outputs.live ?? 0) : '—'} note={nodes ? `${totalOutputs} on nodes, ${mbps(egress)} to platforms` : undefined} />
        </div>
        <Stat label="Users" value={u?.length ?? '—'} note={u ? `${u.filter((x) => x.status === 'blocked').length} blocked, ${u.filter((x) => x.live).length} live` : undefined} />
      </div>
      <Panel title="Outputs by state" aside={<Link to="/admin/network/nodes" className="text-sm text-muted underline-offset-4 hover:text-text hover:underline">Nodes →</Link>}>
        <dl className="flex flex-wrap gap-x-8 gap-y-2" data-testid="overview-states">
          {[...order, ...Object.keys(outputs).filter((s) => !order.includes(s))].map((s) => (
            <div key={s} data-testid={`overview-state-${s}`}>
              <dt className="text-xs text-muted">{s}</dt>
              <dd className="num font-sans text-xl font-semibold">{outputs[s] ?? 0}</dd>
            </div>
          ))}
        </dl>
        {nodes && <p className="mt-3 text-xs text-muted">From the nodes' latest reports.</p>}
      </Panel>
      {grafana && (
        <div className="grid gap-4 md:grid-cols-2">
          {charts.map((c) => (
            <iframe
              key={c.id}
              title={c.title}
              loading="lazy"
              className="h-56 w-full rounded-[0.5rem] border border-line bg-panel"
              src={`${grafana}/d-solo/m15-admin/?orgId=1&panelId=${c.id}&from=now-15m&to=now&refresh=10s`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
